/**
 * FallbackWatchlistPanel Component (Phase 5 - Task 5.4)
 *
 * AI 스크리닝 실패 시 Fallback 종목 표시 컴포넌트
 * - 하드코딩된 7개 종목 사용 (AAPL, MSFT, GOOGL, TSLA, NVDA, BTC/USD, ETH/USD)
 * - 실시간 전략 신호 조회 및 표시
 */

import React, { useEffect, useState } from 'react';
import type { AIStockData } from '../types/watchlist';
import { strategyApi } from '../services/strategyApi';
import StockCard from './StockCard';
import { mapStrategySignalResponse } from '../utils/strategySignals';

interface FallbackWatchlistPanelProps {
  onExecute: (ticker: string) => void;
  executingSymbol: string | null;
}

// Fallback 종목 리스트 (AI 스크리닝 실패 시 사용)
const FALLBACK_STOCKS = ['AAPL', 'MSFT', 'GOOGL', 'TSLA', 'NVDA', 'BTC/USD', 'ETH/USD'];

const FallbackWatchlistPanel: React.FC<FallbackWatchlistPanelProps> = ({
  onExecute,
  executingSymbol,
}) => {
  const [fallbackStocks, setFallbackStocks] = useState<AIStockData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fallback 종목의 실시간 전략 신호 조회
  useEffect(() => {
    const fetchFallbackSignals = async () => {
      setLoading(true);
      setError(null);

      try {
        // 각 Fallback 종목의 전략 신호 조회
        const stocksWithSignals = await Promise.all(
          FALLBACK_STOCKS.map(async (ticker) => {
            try {
              const signal = await strategyApi.getSignal(ticker);

              // AIStockData 형태로 변환
              // Backend 응답을 StrategySignals 형식으로 변환 (Phase 6: 7개 전략)
              const stockData: AIStockData = {
                ticker,
                sector: ticker.includes('/') ? 'Crypto' : 'Technology',
                confidence: 5, // Fallback 종목은 기본 신뢰도 5
                risk_level: 5, // Fallback 종목은 기본 리스크 5
                catalyst: 'Fallback 종목 - AI 스크리닝 실패 시 사용',
                strategy_strength: Math.max(0, Math.min(1, Math.abs(signal.weighted_total ?? signal.strength ?? 0))),
                strategy_signals: mapStrategySignalResponse(signal),
              };

              return stockData;
            } catch (err) {
              // 개별 종목 오류는 무시하고 기본값 반환
              console.error(`Failed to fetch signal for ${ticker}:`, err);
              return {
                ticker,
                sector: ticker.includes('/') ? 'Crypto' : 'Technology',
                confidence: 5,
                risk_level: 5,
                catalyst: 'Fallback 종목 - 신호 조회 실패',
              };
            }
          })
        );

        setFallbackStocks(stocksWithSignals);
      } catch (err) {
        console.error('Error fetching fallback signals:', err);
        setError('Fallback 종목 신호 조회 실패');
      } finally {
        setLoading(false);
      }
    };

    fetchFallbackSignals();

    // 30초마다 신호 업데이트
    const interval = setInterval(fetchFallbackSignals, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="ai-watchlist-panel fallback">
      {/* 경고 메시지 */}
      <div className="fallback-warning">
        <div className="warning-content">
          <h4>AI 스크리닝 실패 - Fallback 종목 사용 중</h4>
          <p>Gemini AI 추천이 실패했습니다. 기본 종목 7개를 사용합니다.</p>
        </div>
      </div>

      <div className="panel-header">
        <h3>Fallback 종목</h3>
      </div>

      {loading ? (
        <div className="loading">Fallback 종목 로딩 중...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="ai-stocks-grid">
          {fallbackStocks.map((stock) => (
            <StockCard
              key={stock.ticker}
              stock={stock}
              onExecute={onExecute}
              executing={executingSymbol === stock.ticker}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FallbackWatchlistPanel;
